'use client';

import React, { FormEvent, useEffect, useState } from 'react'
import CustomContainer from '../../components/common/CustomContainer'
import { AddMoreIcon, BackIcon } from '../../utils/functions/Icons'
import { useNavigate } from 'react-router-dom'
import styles from "../../assets/styles/profile.module.css"
import { FormGroup, FormLabel, MenuItem, Select, TextField } from '@mui/material'
import moment from 'moment'
import { DatePicker } from '@mui/x-date-pickers'
import { toast } from 'react-toastify'
import { addLaundryListApi } from '../../utils/api/services'
import BtnLoader from '../../components/common/BtnLoader'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '../../utils/store'
import { LaundaryItemsReducer } from '../../utils/store/profile/ProfileSlice'

type ItemRow = {
    itemName: string;
    sentQty: string;
    receivedQty: string;
}

type AddLaundryTypes = {
    laundaryName: string;
    date: string | null;
    items: ItemRow[];
}

const AddLaundry = () => {
    const [FormState, setFormState] = useState<AddLaundryTypes>({
        laundaryName: '',
        date: null,
        items: [{ itemName: '', sentQty: '', receivedQty: '' }],
    })
    const [btnLoading, setBtnLoading] = useState(false)
    const { LoginData } = useSelector((state: RootState) => state.auth)
    const { master } = useSelector((state: RootState) => state.profile)
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()

    useEffect(() => {
        dispatch(LaundaryItemsReducer({ path: 'laundaryItems' }))
    }, [dispatch])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        setFormState(prev => ({ ...prev, [name]: value }))
    }

    const handleDateChange = (value: Date | null) => {
        const DateValue = value ? moment(value).format('DD-MM-YYYY') : null
        setFormState(prev => ({ ...prev, date: DateValue }))
    }

    const handleItemRowChange = (index: number, field: keyof ItemRow, value: string) => {
        setFormState(prev => {
            const newArray = prev.items.map((r, i) => i === index ? { ...r, [field]: value } : r)
            return { ...prev, items: newArray }
        })
    }

    const handleAddItemRow = () => {
        const last = FormState.items[FormState.items.length - 1]
        if (last?.itemName) {
            setFormState(prev => ({
                ...prev,
                items: [...prev.items, { itemName: '', sentQty: '', receivedQty: '' }]
            }))
        }
    }

    const handleRemoveItemRow = (index: number) => {
        if (FormState.items.length === 1) return
        setFormState(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }))
    }

    const validateRows = (rows: ItemRow[]) => {
        for (const r of rows) {
            if (!r.itemName) return 'Please select item for all rows.'
            if (!r.sentQty && !r.receivedQty) return 'Please enter sent or received quantity.'
            if (r.sentQty && !/^\d+$/.test(r.sentQty.trim())) return 'Sent quantity must be numeric.'
            if (r.receivedQty && !/^\d+$/.test(r.receivedQty.trim())) return 'Received quantity must be numeric.'
        }
        return null
    }

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        try {
            if (!FormState.laundaryName || !FormState.date) {
                return toast.error('Laundry name and date are required')
            }
            const err = validateRows(FormState.items)
            if (err) return toast.error(err)
            const sendData = {
                companyId: LoginData?.companyId,
                laundaryName: FormState.laundaryName,
                date: FormState.date,
                items: FormState.items,
            }
            setBtnLoading(true)
            const result = await addLaundryListApi(sendData)
            if (result?.success) {
                toast.success(result?.message)
                navigate('/laundry')
            }
        } catch (error: any) {
            toast.error(error?.data?.errors || error?.message)
        } finally {
            setBtnLoading(false)
        }
    }

    return (
        <CustomContainer>
            <div className="AllPageMain">
                <div className="HeadAndButton">
                    <h2 className="flex items-center gap-4 cursor-pointer" onClick={() => navigate(-1)}>
                        <BackIcon />
                        Laundry
                    </h2>
                </div>
                <div className={`${styles.profileMain} mt-8`}>
                    <form onSubmit={handleSubmit}>
                        <div className={styles.otherInformation}>
                            <div className={styles.otherInfoHead}>
                                <h3>Laundry Details</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 md:gap-x-16 gap-y-6">
                                <FormGroup className="customFormGroup">
                                    <FormLabel className="customFormLabel">Laundry Name <span className='errorMsg'>*</span></FormLabel>
                                    <TextField
                                        type="text"
                                        className="custumTextField profileInput w-full"
                                        name="laundaryName"
                                        value={FormState.laundaryName}
                                        onChange={handleInputChange}
                                        placeholder="Enter laundry name"
                                        required
                                    />
                                </FormGroup>
                                <FormGroup className="customFormGroup">
                                    <FormLabel className="customFormLabel">Date <span className='errorMsg'>*</span></FormLabel>
                                    <DatePicker
                                        className="custumTextField profileInput"
                                        value={FormState.date ? moment(FormState.date, 'DD-MM-YYYY') : null}
                                        maxDate={moment(new Date(), 'DD-MM-YYYY')}
                                        format="DD-MM-YYYY"
                                        onChange={(date) => handleDateChange(moment(date)?.toDate())}
                                    />
                                </FormGroup>
                            </div>
                            <div className="mt-6">
                                <div className="flex items-end gap-6 mb-6">
                                    <div className="grid grid-cols-3 gap-x-6 gap-y-6 mt-4 w-full">
                                        {FormState.items.map((row, index) => (
                                            <React.Fragment key={index}>
                                                <FormGroup className="customFormGroup">
                                                    <FormLabel className="customFormLabel">Item <span className='errorMsg'>*</span></FormLabel>
                                                    <Select
                                                        value={row.itemName}
                                                        onChange={(e) => handleItemRowChange(index, 'itemName', e.target.value)}
                                                        className="custumTextField profileInput w-full"
                                                    >
                                                        <MenuItem value=""><em>None</em></MenuItem>
                                                        {master?.laundaryItems?.map((item: any, idx: number) => (
                                                            <MenuItem key={idx} value={item?.itemName}>{item?.itemName}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormGroup>
                                                <FormGroup className="customFormGroup">
                                                    <FormLabel className="customFormLabel">Sent Qty</FormLabel>
                                                    <TextField
                                                        type="text"
                                                        className={`custumTextField profileInput w-full`}
                                                        value={row.sentQty}
                                                        onChange={(e) => handleItemRowChange(index, 'sentQty', e.target.value)}
                                                        inputProps={{ inputMode: 'numeric' }}
                                                    />
                                                </FormGroup>
                                                <FormGroup className="customFormGroup">
                                                    <FormLabel className="customFormLabel">Received Qty</FormLabel>
                                                    <div className="flex items-center gap-2">
                                                        <TextField
                                                            type="text"
                                                            className={`custumTextField profileInput w-full`}
                                                            value={row.receivedQty}
                                                            onChange={(e) => handleItemRowChange(index, 'receivedQty', e.target.value)}
                                                            inputProps={{ inputMode: 'numeric' }}
                                                        />
                                                        {FormState.items.length > 1 && (
                                                            <button type="button" onClick={() => handleRemoveItemRow(index)} className="text-sm p-2">Remove</button>
                                                        )}
                                                    </div>
                                                </FormGroup>
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    <button type="button" className={styles.AddMoreBtn} onClick={handleAddItemRow}>
                                        <AddMoreIcon />
                                    </button>
                                </div>
                            </div>
                        </div>
                        <button className="fillButton mt-8" type="submit">
                            {btnLoading && <BtnLoader />} Save Details
                        </button>
                    </form>
                </div>
            </div>
        </CustomContainer>
    )
}

export default AddLaundry
